import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Stack, Group } from '@mantine/core';
import { NavLink, useLocation } from 'react-router';
import { IconChevronDown } from '@tabler/icons-react';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { SidebarLink, SidebarItem as SidebarItemType, DIVIDER } from './types';
import { SidebarDivider } from './SidebarDivider';
import { CountBadge } from './CountBadge';
import { FONT_WEIGHTS } from '../../theme/theme';
import { useFileDragDrop } from '../../../data-capture/file-management/context/FileDragDropContext';
import { createDragElement } from '../../../data-capture/file-management/utils/createDragElement';

type SidebarItemProps = {
  item: SidebarItemType;
  activeUrl: string;
  level?: number;
  onSelect?: () => void;
};

export function SidebarItem({ item, activeUrl, level = 0, onSelect }: SidebarItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const { pathname } = useLocation();
  const dragDropContext = useFileDragDrop();
  const cq = useContainerQuery();

  const link = item === DIVIDER ? null : (item as SidebarLink);
  const hasChildren = link?.children && link.children.length > 0;
  const isActive = link ? pathname === link.href : false;
  const isFolderLink = link?.href.includes('/folder/') ?? false;
  const folderId = isFolderLink && link ? link.href.split('/folder/')[1] : null;

  useEffect(() => {
    if (link?.defaultExpanded !== undefined) {
      setIsExpanded(link.defaultExpanded);
    }
  }, [link?.defaultExpanded]);

  const handleToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (dragDropContext?.draggedItem && link && (isFolderLink || link.href.includes('/files'))) {
        const targetParentId = link.href.endsWith('/files') ? null : folderId;
        if (dragDropContext.canDropOnTarget(targetParentId)) {
          e.preventDefault();
          setIsDraggedOver(true);
        }
      }
    },
    [dragDropContext, isFolderLink, link, folderId],
  );

  const handleDragLeave = useCallback(() => {
    setIsDraggedOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggedOver(false);

      if (dragDropContext?.draggedItem && link) {
        try {
          const targetParentId = link.href.endsWith('/files') ? null : folderId;
          await dragDropContext.handleMoveFile(dragDropContext.draggedItem, targetParentId);
        } finally {
          if (dragDropContext.setDraggedItem) {
            dragDropContext.setDraggedItem(null);
          }
          if (dragDropContext.setIsDraggedOver) {
            dragDropContext.setIsDraggedOver(null);
          }
        }
      }
    },
    [dragDropContext, folderId, link],
  );

  const folder = dragDropContext?.allFiles.find(file => file.id === folderId && file.isFolder);

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (dragDropContext && folder) {
        e.dataTransfer.effectAllowed = 'move';
        dragDropContext.setDraggedItem(folder);
        createDragElement(folder, e);
      }
    },
    [dragDropContext, folder],
  );

  const handleDragEnd = useCallback(() => {
    if (dragDropContext) {
      dragDropContext.setDraggedItem(null);
      dragDropContext.setIsDraggedOver(null);
    }
  }, [dragDropContext]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (dragDropContext?.draggedItem) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onSelect?.();
    },
    [dragDropContext?.draggedItem, onSelect],
  );

  const labelWithBadge = useMemo(
    () =>
      link && link.count !== undefined && link.count > 0 ? (
        <Group gap="xxxs" align="center">
          <span>{link.label}</span>
          <CountBadge count={link.count} />
        </Group>
      ) : (
        link?.label
      ),
    [link],
  );

  if (!link) {
    return <SidebarDivider />;
  }

  const leftSection = link.icon && (
    <Box
      w={20}
      h={20}
      bg="white"
      bd="1px solid"
      c="gray.3"
      style={{ borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      p="xxxs"
    >
      <link.icon size={10} color="var(--mantine-color-gray-4)" />
    </Box>
  );

  const rightSection = hasChildren && (
    <Box
      w={24}
      h={24}
      bg="contentBackground"
      onClick={handleToggle}
      role="button"
      aria-label={isExpanded ? `Collapse ${link.label}` : `Expand ${link.label}`}
      aria-expanded={isExpanded}
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          setIsExpanded(!isExpanded);
        }
      }}
      style={{
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
        cursor: 'pointer',
      }}
    >
      <IconChevronDown size={16} color="var(--mantine-color-gray-6)" />
    </Box>
  );

  const textColor =
    level === 0 && !isActive ? 'var(--mantine-color-gray-10)' : 'var(--mantine-color-gray-8)';

  return (
    <Box>
      <NavLink
        to={link.href}
        style={{ textDecoration: 'none' }}
        aria-current={isActive ? 'page' : undefined}
        data-testid="sidebar-link"
        onClick={handleClick}
        draggable={isFolderLink && folder && !!dragDropContext}
        onDragStart={isFolderLink && folder ? handleDragStart : undefined}
        onDragEnd={isFolderLink && folder ? handleDragEnd : undefined}
      >
        <Box
          p="xxxs"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            borderRadius: 4,
            backgroundColor:
              isDraggedOver || isActive ? 'var(--mantine-color-brand-3)' : 'transparent',
            border: isDraggedOver ? '2px dashed var(--mantine-color-brand-6)' : 'none',
            fontSize:
              level === 0 ? 'var(--mantine-fontSizes-textLg)' : 'var(--mantine-fontSizes-textMd)',
            fontWeight: isActive ? FONT_WEIGHTS.bold : FONT_WEIGHTS.semibold,
            color: textColor,
            transition: 'background-color 0.2s ease, border 0.2s ease',
            cursor: isFolderLink && folder && dragDropContext ? 'grab' : 'pointer',
          }}
        >
          <Group gap="xxxs" align="center">
            {leftSection}
            <Box style={{ flex: 1 }}>{labelWithBadge}</Box>
            {rightSection}
          </Group>
        </Box>
      </NavLink>

      {hasChildren && isExpanded && link.children && (
        <Box
          style={{ borderLeft: '1px solid var(--mantine-color-gray-4)' }}
          ml="xxxs"
          pl={cq({ base: 'xs', xl: 'sm' })}
          mt={level === 0 ? 'sm' : 'xs'}
        >
          <Stack gap="xxs">
            {link.children.map((child, index) => (
              <SidebarItem
                key={child === DIVIDER ? `divider-${index}` : (child as SidebarLink).href}
                item={child}
                activeUrl={activeUrl}
                level={level + 1}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
